import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import LoadingSpinner from './components/LoadingSpinner';
import { colors } from './theme';
import { formatRelativeTime, getStatusBadgeStyle, POC_FIELD_LABELS, POC_FIELD_SECTIONS } from './pocTestUtils';
import { isTestGpv } from './roles';
import TestGpvBanner from './TestGpvBanner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function PocTestDevicePage() {
  const { id } = useParams();
  const { user, isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const allowed = isTestGpv(user);

  useEffect(() => {
    if (!isLoaded || !allowed || !id) return;

    let isMounted = true;
    async function fetchDevice() {
      setStatus('loading');
      setError('');
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Authentication required.');
        }
        const response = await fetch(`${API_BASE}/poc-test/devices/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (response.status === 404) {
          throw new Error('Device not found.');
        }
        if (response.status === 403) {
          throw new Error('You do not have access to this device.');
        }
        if (!response.ok) {
          throw new Error(`Failed to load device (${response.status})`);
        }
        const payload = await response.json();
        if (!isMounted) return;
        setDevice(payload.device || null);
        setStatus('ready');
      } catch (err) {
        if (!isMounted) return;
        setError(err.message || 'Failed to load device.');
        setStatus('error');
      }
    }
    fetchDevice();
    return () => {
      isMounted = false;
    };
  }, [allowed, getToken, id, isLoaded]);

  if (!isLoaded) {
    return (
      <div style={centeredPage}>
        <LoadingSpinner size={48} />
      </div>
    );
  }

  if (!isSignedIn || !allowed) {
    return <Navigate to="/" replace />;
  }

  const ingestedIso = device?.ingested_at || '';
  const ingestedRelative = formatRelativeTime(ingestedIso);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.sectionBackground }}>
      <header style={headerStyle}>
        <Link to="/" style={backLink}>&larr; Back to POC Test Devices</Link>
        <h1 style={headerTitle}>POC Test Device Overview</h1>
      </header>
      <TestGpvBanner />
      <main style={mainContainer}>
        {status === 'loading' && (
          <div style={loadingContainer}>
            <LoadingSpinner size={48} />
          </div>
        )}
        {status === 'error' && (
          <div style={errorCard}>{error}</div>
        )}
        {status === 'ready' && device && (
          <>
            <section style={summaryCard}>
              <div>
                <h2 style={summaryTitle}>{device.serialNumber || 'Unknown Serial'}</h2>
                <p style={summaryMeta}>Product Number: {device.productNumber || 'N/A'}</p>
                <p style={summaryMeta}>Service Tag: {device.serviceTagNumber || 'N/A'}</p>
                <p style={summaryMeta}>Workorder: {device.workorder || 'N/A'}</p>
              </div>
              <div style={summaryStatus}>
                <div>
                  <span style={summaryLabel}>Test Status</span>
                  <span style={getStatusBadgeStyle(device.testStatus)}>
                    {(device.testStatus || 'unknown').toString().toUpperCase()}
                  </span>
                </div>
                <div>
                  <span style={summaryLabel}>Ingested</span>
                  <span title={ingestedIso} style={summaryValue}>
                    {ingestedRelative}
                  </span>
                </div>
              </div>
            </section>

            {POC_FIELD_SECTIONS.map((section) => (
              <section key={section.title} style={detailCard}>
                <h3 style={sectionHeading}>{section.title}</h3>
                <div style={detailGrid}>
                  {section.keys.map((key) => (
                    <div key={key} style={detailItem}>
                      <span style={detailLabel}>{POC_FIELD_LABELS[key] || key}</span>
                      <span style={detailValue}>
                        {key === 'testStatus' ? (
                          <span style={getStatusBadgeStyle(device[key])}>
                            {(device[key] || 'unknown').toString().toUpperCase()}
                          </span>
                        ) : (
                          device[key] || 'N/A'
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}

const centeredPage = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: colors.sectionBackground
};

const headerStyle = {
  backgroundColor: colors.sectionBackground,
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const backLink = {
  color: colors.background,
  textDecoration: 'none'
};

const headerTitle = {
  margin: 0,
  color: colors.background,
  fontSize: '1.75rem'
};

const mainContainer = {
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '2rem 1rem 4rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
};

const loadingContainer = {
  display: 'flex',
  justifyContent: 'center',
  padding: '4rem 0'
};

const errorCard = {
  backgroundColor: colors.background,
  borderRadius: '12px',
  padding: '2rem',
  color: colors.danger,
  fontWeight: 600,
  textAlign: 'center'
};

const summaryCard = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1.5rem',
  flexWrap: 'wrap',
  backgroundColor: colors.background,
  borderRadius: '16px',
  padding: '1.75rem',
  boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
};

const summaryTitle = {
  margin: 0,
  fontSize: '2rem',
  color: colors.primary,
  fontWeight: 700
};

const summaryMeta = {
  margin: '0.2rem 0',
  color: colors.muted
};

const summaryStatus = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  alignItems: 'flex-end'
};

const summaryLabel = {
  display: 'block',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: colors.muted,
  marginBottom: '0.25rem'
};

const summaryValue = {
  fontSize: '1rem',
  color: colors.primary,
  fontWeight: 600
};

const detailCard = {
  backgroundColor: colors.background,
  borderRadius: '16px',
  padding: '1.5rem',
  boxShadow: '0 12px 24px rgba(0,0,0,0.08)'
};

const sectionHeading = {
  margin: '0 0 1rem',
  fontSize: '1.25rem',
  color: colors.primary
};

const detailGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1rem'
};

const detailItem = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem'
};

const detailLabel = {
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: colors.muted
};

const detailValue = {
  fontSize: '1rem',
  fontWeight: 600,
  color: colors.sectionBackground
};
